import type { AccountId, AccountSummary, CreateAccount } from "@trader/shared/Money"
import type { AccountState } from "./accountStore.ts"

// The signed-in user's paper accounts and which one the Trading panel follows.
// The choice is this browser's preference (per user); when it is gone (reset,
// another browser), the newest active account is picked. An ended session
// (docs/design/0008) stays selectable, read-only. Plain TS: the store takes its
// API and storage as dependencies.

export interface AccountsState {
  readonly status: "idle" | "loading" | "ready" | "failed"
  /** Newest first, archived ones included (the server's order). */
  readonly accounts: ReadonlyArray<AccountSummary>
  readonly selectedId: AccountId | null
}

/** The accounts the selector offers for trading: everything not archived. */
export const activeAccounts = (accounts: ReadonlyArray<AccountSummary>): ReadonlyArray<AccountSummary> =>
  accounts.filter((account) => account.status !== "Archived")

/** A session that was ended (archived with `endedAt`); a reset archives without ending. */
export const isEnded = (account: AccountSummary): boolean => account.status === "Archived" && account.endedAt !== null

/** The selector's "Ended sessions": read-only, opened on their summary. */
export const endedAccounts = (accounts: ReadonlyArray<AccountSummary>): ReadonlyArray<AccountSummary> => accounts.filter(isEnded)

/** The preferred account when it is still active (or an ended session), else the newest active one, else none. */
export const chooseAccount = (accounts: ReadonlyArray<AccountSummary>, preferred: string | null): AccountId | null => {
  const kept = accounts.find((account) => account.id === preferred && (account.status !== "Archived" || isEnded(account)))
  return kept?.id ?? activeAccounts(accounts)[0]?.id ?? null
}

/**
 * The account whose live topic to follow: the selected one, unless the list knows it
 * is archived (the server has no topic for it; an ended session is read over HTTP).
 */
export const followedAccountId = (state: AccountsState): AccountId | null => {
  const selected = state.accounts.find((account) => account.id === state.selectedId)
  return selected !== undefined && selected.status === "Archived" ? null : state.selectedId
}

export interface AccountsApi {
  readonly list: () => Promise<ReadonlyArray<AccountSummary>>
  readonly create: (request: CreateAccount) => Promise<AccountSummary>
  readonly reset: (accountId: AccountId) => Promise<AccountSummary>
  readonly end: (accountId: AccountId) => Promise<AccountSummary>
  readonly rename: (accountId: AccountId, name: string) => Promise<AccountSummary>
  readonly remove: (accountId: AccountId) => Promise<void>
}

export interface PreferenceStorage {
  readonly get: (key: string) => string | null
  readonly set: (key: string, value: string) => void
}

export interface AccountsStore {
  readonly getState: () => AccountsState
  readonly subscribe: (listener: () => void) => () => void
  /** Starts (or restarts, for another user) and loads the list. */
  readonly start: (userId: number) => void
  readonly reload: () => Promise<void>
  readonly select: (accountId: AccountId) => void
  /** Creates an account and selects it. Rejects with the API error. */
  readonly create: (request: CreateAccount) => Promise<AccountSummary>
  /** Resets an account (archives it, opens a fresh copy) and selects the copy. */
  readonly reset: (accountId: AccountId) => Promise<AccountSummary>
  /** Ends an account's session (docs/design/0008) and keeps it selected, now read-only. Rejects with the API error. */
  readonly end: (accountId: AccountId) => Promise<AccountSummary>
  /** Renames an account (docs/design/0007). Rejects with the API error. */
  readonly rename: (accountId: AccountId, name: string) => Promise<AccountSummary>
  /**
   * Deletes an ended account (docs/design/0015) and drops it from the list; if it was
   * selected, the newest active account is. Rejects with the API error.
   */
  readonly remove: (accountId: AccountId) => Promise<void>
  /** The followed account vanished (another user's id, deleted): pick another. */
  readonly forget: (accountId: AccountId) => void
}

const keyFor = (userId: number) => `trader.trading.account.${userId}`

export const createAccountsStore = (api: AccountsApi, storage: PreferenceStorage): AccountsStore => {
  let state: AccountsState = { status: "idle", accounts: [], selectedId: null }
  let userId: number | null = null
  let generation = 0
  const listeners = new Set<() => void>()

  const set = (next: AccountsState) => {
    state = next
    for (const listener of listeners) listener()
  }

  const remember = (accountId: AccountId | null) => {
    if (userId !== null && accountId !== null) storage.set(keyFor(userId), accountId)
  }

  const withList = (accounts: ReadonlyArray<AccountSummary>, preferred: string | null): AccountsState => {
    const selectedId = chooseAccount(accounts, preferred)
    remember(selectedId)
    return { status: "ready", accounts, selectedId }
  }

  const reload = async (): Promise<void> => {
    const mine = generation
    if (state.status !== "ready") set({ ...state, status: "loading" })
    try {
      const accounts = await api.list()
      if (mine !== generation) return
      const preferred = state.selectedId ?? (userId === null ? null : storage.get(keyFor(userId)))
      set(withList(accounts, preferred))
    } catch (error) {
      if (mine === generation) set({ ...state, status: state.status === "ready" ? "ready" : "failed" })
      throw error
    }
  }

  /** Puts a summary at the top of the list (replacing an older copy) and selects it. */
  const upsertAndSelect = (summary: AccountSummary, archived: AccountId | null) => {
    const others = state.accounts
      .filter((account) => account.id !== summary.id)
      .map((account) => (account.id === archived ? { ...account, status: "Archived" as const } : account))
    remember(summary.id)
    set({ status: "ready", accounts: [summary, ...others], selectedId: summary.id })
  }

  return {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    start: (id) => {
      if (userId === id && state.status !== "idle") return
      userId = id
      generation++
      set({ status: "idle", accounts: [], selectedId: null })
      reload().catch(() => undefined)
    },
    reload,
    select: (accountId) => {
      if (accountId === state.selectedId) return
      remember(accountId)
      set({ ...state, selectedId: accountId })
    },
    create: async (request) => {
      const summary = await api.create(request)
      upsertAndSelect(summary, null)
      return summary
    },
    reset: async (accountId) => {
      const summary = await api.reset(accountId)
      upsertAndSelect(summary, accountId)
      return summary
    },
    end: async (accountId) => {
      const summary = await api.end(accountId)
      // In place (it may have been dropped meanwhile, when its topic closed first).
      const known = state.accounts.some((account) => account.id === summary.id)
      const accounts = known ? state.accounts.map((account) => (account.id === summary.id ? summary : account)) : [summary, ...state.accounts]
      remember(summary.id)
      set({ status: "ready", accounts, selectedId: summary.id })
      return summary
    },
    rename: async (accountId, name) => {
      const summary = await api.rename(accountId, name)
      set({ ...state, accounts: state.accounts.map((account) => (account.id === summary.id ? summary : account)) })
      return summary
    },
    remove: async (accountId) => {
      await api.remove(accountId)
      const accounts = state.accounts.filter((account) => account.id !== accountId)
      const selectedId = state.selectedId === accountId ? chooseAccount(accounts, null) : state.selectedId
      if (selectedId !== state.selectedId) remember(selectedId)
      set({ ...state, accounts, selectedId })
    },
    forget: (accountId) => {
      if (state.selectedId !== accountId) return
      const accounts = state.accounts.filter((account) => account.id !== accountId)
      set({ ...state, accounts, selectedId: chooseAccount(accounts, null) })
      remember(state.selectedId)
    }
  }
}

/** The followed account's replay time, from its live state or else its list entry; null: don't clip. */
export const replayEnd = (live: AccountState | null, accounts: AccountsState, sync: boolean): number | null => {
  if (!sync) return null
  const summary = live?.summary ?? accounts.accounts.find((account) => account.id === accounts.selectedId) ?? null
  return summary !== null && summary.clock._tag === "Replay" && summary.status !== "Archived" ? summary.clock.now : null
}
